import type { User } from '@supabase/supabase-js';
import type { Theme } from '../types/theme';
import type { Viewport } from '../components/ViewportSwitcher';
import { supabase } from './supabase';
import { normalizeTheme } from './themeTools';

type PageType = 'landing' | 'dashboard';
type ExportKind = 'design_md' | 'prompt' | 'bundle';
type SourceKind = 'design_md' | 'screenshot' | 'url' | 'custom';

export interface ShortlistRecord {
  id: string;
  theme_id: string;
  page_type: PageType;
  viewport: Viewport;
  note: string;
  created_at: string;
  updated_at: string;
}

export interface ExportHistoryRecord {
  id: string;
  theme_id: string;
  theme_name: string;
  page_type: PageType;
  export_kind: ExportKind;
  content: string;
  created_at: string;
}

export interface CustomThemeRecord {
  id: string;
  name: string;
  source_kind: SourceKind;
  source_label: string;
  theme: Theme;
  design_md: string;
  created_at: string;
  updated_at: string;
}

interface SaveCustomThemeInput {
  designMd: string;
  name: string;
  sourceKind: SourceKind;
  sourceLabel: string;
  theme: Theme;
  userId: string;
}

interface SaveExtractionInput {
  designMd: string;
  sourceKind: Exclude<SourceKind, 'custom'>;
  sourceLabel: string;
  theme: Theme;
  userId: string;
}

export async function ensureProfile(user: User): Promise<void> {
  if (!supabase) {
    return;
  }

  await supabase.from('profiles').upsert({
    id: user.id,
    email: user.email,
    full_name: getUserDisplayName(user),
    avatar_url: typeof user.user_metadata?.avatar_url === 'string' ? user.user_metadata.avatar_url : null,
  });
}

export async function fetchShortlistRecords(userId: string): Promise<ShortlistRecord[]> {
  if (!supabase) {
    return [];
  }

  const { data, error } = await supabase
    .from('shortlists')
    .select('id, theme_id, page_type, viewport, note, created_at, updated_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });

  if (error) {
    throw error;
  }

  return (data || []) as ShortlistRecord[];
}

export async function syncShortlistRecord(
  userId: string,
  themeId: string,
  pageType: PageType,
  viewport: Viewport,
): Promise<void> {
  if (!supabase) {
    return;
  }

  const { error } = await supabase.from('shortlists').upsert({
    user_id: userId,
    theme_id: themeId,
    page_type: pageType,
    viewport,
  }, { onConflict: 'user_id,theme_id' });

  if (error) {
    throw error;
  }
}

export async function removeShortlistRecord(userId: string, themeId: string): Promise<void> {
  if (!supabase) {
    return;
  }

  const { error } = await supabase
    .from('shortlists')
    .delete()
    .eq('user_id', userId)
    .eq('theme_id', themeId);

  if (error) {
    throw error;
  }
}

export async function clearShortlistRecords(userId: string): Promise<void> {
  if (!supabase) {
    return;
  }

  const { error } = await supabase
    .from('shortlists')
    .delete()
    .eq('user_id', userId);

  if (error) {
    throw error;
  }
}

export async function saveExportHistory(
  userId: string,
  theme: Theme,
  pageType: PageType,
  exportKind: ExportKind,
  content: string,
): Promise<void> {
  if (!supabase) {
    return;
  }

  const { error } = await supabase.from('export_history').insert({
    user_id: userId,
    theme_id: theme.id,
    theme_name: theme.name,
    page_type: pageType,
    export_kind: exportKind,
    content,
  });

  if (error) {
    throw error;
  }
}

export async function fetchExportHistory(userId: string): Promise<ExportHistoryRecord[]> {
  if (!supabase) {
    return [];
  }

  const { data, error } = await supabase
    .from('export_history')
    .select('id, theme_id, theme_name, page_type, export_kind, content, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(30);

  if (error) {
    throw error;
  }

  return (data || []) as ExportHistoryRecord[];
}

export async function saveCustomTheme(input: SaveCustomThemeInput): Promise<void> {
  if (!supabase) {
    return;
  }

  const { error } = await supabase.from('custom_themes').insert({
    user_id: input.userId,
    name: input.name,
    source_kind: input.sourceKind,
    source_label: input.sourceLabel,
    theme: input.theme,
    design_md: input.designMd,
  });

  if (error) {
    throw error;
  }
}

export async function fetchCustomThemes(userId: string): Promise<CustomThemeRecord[]> {
  if (!supabase) {
    return [];
  }

  const { data, error } = await supabase
    .from('custom_themes')
    .select('id, name, source_kind, source_label, theme, design_md, created_at, updated_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data || []).map((record) => ({
    ...(record as Omit<CustomThemeRecord, 'theme'> & { theme: unknown }),
    theme: normalizeTheme((record as { theme: unknown }).theme),
  }));
}

export async function saveStyleExtraction(input: SaveExtractionInput): Promise<void> {
  if (!supabase) {
    return;
  }

  const { error } = await supabase.from('style_extractions').insert({
    user_id: input.userId,
    source_kind: input.sourceKind,
    source_label: input.sourceLabel,
    theme: input.theme,
    design_md: input.designMd,
  });

  if (error) {
    throw error;
  }
}

function getUserDisplayName(user: User): string | null {
  const metadataName = user.user_metadata?.full_name || user.user_metadata?.name;
  return typeof metadataName === 'string' ? metadataName : null;
}
